function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { onHidden } from 'web-vitals/dist/lib/onHidden';
import { generateUniqueID } from 'web-vitals/dist/lib/generateUniqueID';
/** @typedef {Object<string,any>} Result */

/** @typedef {import('web-vitals').Metric | Object<string,any>} Metric */

/** @typedef {{ effectiveType: 'slow-2g' | '2g' | '3g' | '4g', rtt: number, downlink: number }} NetworkInformation */

/**
 * Create Web Vitals API reporter, that accepts `Metric` values and sends it to `url`
 * using `navigator.sendBeacon` when available or fallbacks back to XMLHttpRequest.
 *
 * The function sends request only once.
 * Use `onSend` to implement a custom logic.
 *
 * @param {string} url
 * @param {{ initial?: object, mapMetric?: (metric: Metric, result: Result) => Result, beforeSend?: (result: Result) => Result, onSend?: (url: string, result: Result) => any }} [opts]
 * @return {(metric: Metric) => void}
 */

export function createApiReporter(url) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var isSent = false;
  var isCalled = false;

  var result =
  /** @type {Result} */
  _objectSpread({
    id: generateUniqueID()
  }, opts.initial);

  var sendValues = function sendValues() {
    if (isSent) return; // data is already sent

    if (!isCalled) return; // no data collected

    result.duration = now();

    if (opts.beforeSend) {
      result = _objectSpread(_objectSpread({}, result), opts.beforeSend(result));
    }

    isSent = true;

    if (opts.onSend) {
      opts.onSend(url, result);
    } else {
      if (typeof navigator === 'undefined') return;
      if (navigator.sendBeacon) return navigator.sendBeacon(url, JSON.stringify(result));
      var client = new XMLHttpRequest();
      client.open('POST', url, false); // third parameter indicates sync xhr

      client.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
      client.send(JSON.stringify(result));
    }
  };

  var mapMetric = opts.mapMetric || function (metric) {
    var isWebVital = ['FCP', 'TTFB', 'LCP', 'CLS', 'FID'].indexOf(metric.name) !== -1;
    return _defineProperty({}, metric.name, isWebVital ? round(metric.value, metric.name === 'CLS' ? 4 : 0) : metric.value);
  };
  /** @param {Metric} metric */


  var report = function report(metric) {
    if (!isCalled) isCalled = true;
    result = _objectSpread(_objectSpread({}, result), mapMetric(metric, result));
  }; // should be the last call to capture latest CLS


  setTimeout(function () {
    // Safari does not fire "visibilitychange" on the tab close
    // So we have 2 options: loose Safari data, or loose LCP/CLS that depends on "visibilitychange" logic.
    // Current solution: if LCP/CLS supported, use `onHidden` otherwise, use `pagehide` to fire the callback in the end.
    //
    // More details: https://github.com/treosh/web-vitals-reporter/issues/3
    var supportedEntryTypes = PerformanceObserver && PerformanceObserver.supportedEntryTypes || [];
    var isLatestVisibilityChangeSupported = supportedEntryTypes.indexOf('layout-shift') !== -1;

    if (isLatestVisibilityChangeSupported) {
      onHidden(function (_ref2) {
        var isUnloading = _ref2.isUnloading;
        if (isUnloading) sendValues();
      });
    } else {
      addEventListener('pagehide', sendValues, {
        capture: true,
        once: true
      });
    }
  });
  return report;
}
/**
 * Get device information.
 * - Effective connection type: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 * - Device memory: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/deviceMemory
 */

export function getDeviceInfo() {
  var nav =
  /** @type {null | (Navigator & { deviceMemory: number, connection: NetworkInformation })} */
  typeof navigator === 'undefined' ? null : navigator;
  var conn = nav && nav.connection ? nav.connection : null;
  return {
    url: location ? location.href : null,
    referrer: document ? document.referrer : null,
    userAgent: nav ? nav.userAgent : null,
    memory: nav ? nav.deviceMemory : null,
    cpus: nav ? nav.hardwareConcurrency : null,
    connection: conn ? {
      effectiveType: conn.effectiveType,
      rtt: conn.rtt,
      downlink: conn.downlink
    } : null
  };
}
/**
 * Get time since a session started.
 */

function now() {
  var perf = typeof performance === 'undefined' ? null : performance;
  return perf && perf.now ? round(perf.now()) : null;
}
/**
 * Round, source: https://stackoverflow.com/a/18358056
 *
 * @param {number} val
 * @param {number} [precision]
 * @return {number}
 */


function round(val) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  // @ts-ignore
  return +(Math.round("".concat(val, "e+").concat(precision)) + "e-".concat(precision));
}